import { z } from 'zod';

import { BillingCurrencySchema } from '../../billing/currency/currency';
import { DirectPaymentDetailsSchema } from '../../billing/direct-payment-details/direct-payment-details';
import { LocaleSchema } from '../../common/locale/locale';
import { AccentColorSchema } from '../accent-color/accent-color';
import { BrandLegalEntitySchema } from '../brand-legal-entity/brand-legal-entity';
import { DatePatternSchema } from '../date-format/date-pattern/date-pattern';
import { TimeFormatSchema } from '../time-format/time-format';
import { WeekStartDaySchema } from '../week-start/week-start';

/**
 * Every setting the platform publishes to an **unauthenticated** client (P22c2).
 *
 * ## Why the client reads settings at all
 *
 * U16 says the same build serves every brand and that brand facts come from the
 * `brand.*` group, never from code — and until this contract existed the mobile
 * app had no way to read a single setting, so "from settings" stopped at the
 * api's own boundary. A member app that cannot ask what currency the deployment
 * quotes in has to either hardcode one (the brand literal U16 forbids) or draw a
 * bare number and hope, which is what P22c2's discounts screen would otherwise
 * have done for a `fixed` grant.
 *
 * ## The keys are the REGISTRY's, dotted, and that is deliberate
 *
 * A camelCase mirror (`billingCurrency`) reads more like a client object and is
 * exactly the drift to avoid: the dotted key is the setting's identity — it is
 * what `getSetting` takes, what the admin screen addresses, and what the value
 * row is stored under — so a second spelling would be a second name for one
 * thing, wrong the first time somebody renames a key.
 *
 * ## What is in here, and what is deliberately NOT
 *
 * Exposure is **opt-in per definition** (`memberVisible` on the api's setting
 * definition, defaulted false), and the api's own spec asserts this shape's keys
 * are exactly the registry's opted-in set — so adding one is a deliberate
 * two-file diff and a leak by omission cannot compile.
 *
 * Left out, each for a reason rather than an oversight:
 * - `brand.maxMediaAssets` — a staff-side ceiling on the brand library.
 * - `courses.enabled` — the client learns it from the api's 404 **code**, which
 *   is the design P22c1 settled; publishing it here would be a second answer to
 *   one question.
 * - Anything gender-scoped — refused at the definition, because this read has no
 *   session to scope by (U15).
 *
 * `brand.accentColor` was on this list until P28 ("this app themes from tokens").
 * It is published now because the registry ships a `color` control staff can set,
 * and a control nothing reads is a dead control (U0): the value is a runtime
 * override of exactly one token pair, and `''` — the default — is the build's own
 * token (U6). Nothing else in the palette is reachable from here.
 *
 * `brand.iosAppId` / `brand.androidPackage` were on that list until D13 ("a
 * client already knows its own bundle id"). The marketing site (`apps/web`) is
 * the client that does NOT: its store badges are built from them, and the
 * alternative was the store link as a literal in a client bundle — the exact
 * thing U16 forbids. `''` means "no release in that store yet", and the site
 * draws no badge for it. The `brand.web.*` keys arrived with them: what the
 * site says about itself to a search engine is a brand fact, never code.
 */
export const PublicAppSettingsSchema = z
  .object({
    /** The code every amount the platform quotes is denominated in (ISO 4217). */
    'billing.currency': BillingCurrencySchema,
    /**
     * Whether staff may record a direct (non-store) payment at all (P23c1).
     *
     * Published because the member screen must not describe a channel the
     * platform has closed: with this `false` there is nobody to receive the
     * transfer or confirm it, so instructions would send a member to make a
     * payment that could never become a subscription. The client learns it here
     * rather than from a refusal, because there is no member endpoint to be
     * refused by — the direct channel is staff-operated end to end.
     */
    'billing.direct.enabled': z.boolean(),
    /**
     * Where to send the money — four named facts, never a paragraph.
     *
     * All-blank is the ordinary unconfigured state and the screen falls back to
     * the brand's support contact; see the schema for why nothing here is
     * required and why it is not translatable.
     */
    'billing.direct.paymentDetails': DirectPaymentDetailsSchema,
    /** Per-locale (N5): resolved from the request's `Accept-Language`. */
    'brand.name': z.string(),
    /** `''` means "not configured yet" — a client renders no mailto rather than a broken one. */
    'brand.supportEmail': z.string(),
    /** Open by nature: which links a brand has is a brand fact (U16). */
    'brand.links': z.record(z.string(), z.string()),
    /** RevenueCat's public SDK key per platform; each app reads only its own, and `''` means that
     * platform sells nothing in-app (the direct channel, if open, is its only door). */
    'brand.iosRcPublicKey': z.string(),
    'brand.androidRcPublicKey': z.string(),
    /**
     * The day a calendar week starts on in this deployment's market (P25b1).
     *
     * Published rather than derived from the reader's locale, because CLDR
     * answers a question about the **locale** and this is a fact about **where
     * the platform operates**: a Riyadh brand with English-speaking staff would
     * otherwise be handed Monday. See {@link WeekStartDaySchema} for the whole
     * argument. Read by the admin's availability calendar today; the member
     * meeting screens (P25e) want the same answer, and a second derivation would
     * be a second answer.
     */
    'brand.weekStartsOn': WeekStartDaySchema,
    /**
     * The brand mark, as the id of the `brand_asset` behind it (D10) — and its
     * dark-theme variant, `null` when the deployment uploaded only one.
     *
     * Published for two facts a client cannot otherwise know: **whether** there
     * is a logo to draw (so the shell draws the brand's name instead of a broken
     * image), and **which** one — the id is the cache key on
     * `GET /media/brand-logo?variant=…&v=<id>`, which is where the bytes come
     * from. The id is never turned into a storage url by a client (N12): that
     * route mints the presigned GET per request, and a dark variant that is
     * `null` answers with the light mark.
     */
    'brand.logoMediaId': z.uuid().nullable(),
    'brand.logoDarkMediaId': z.uuid().nullable(),
    /**
     * The operating company (P28) — drawn on the legal pages and the about
     * surfaces; all-blank until a deployment fills it in.
     */
    'brand.legalEntity': BrandLegalEntitySchema,
    /**
     * How every member-facing date and time is rendered (P28). A brand fact,
     * not a locale one — see {@link WeekStartDaySchema} — and rendered by the
     * same `formatDatePattern` / `formatClock` the admin preview uses (U3).
     */
    'brand.dateFormat': DatePatternSchema,
    'brand.timeFormat': TimeFormatSchema,
    /**
     * The locale a member gets before choosing one (P28). The phone keys its
     * very first launch's direction to it; a stored preference always wins.
     */
    'brand.defaultLocale': LocaleSchema,
    /** The runtime accent override, `''` for the build's token — see the header. */
    'brand.accentColor': AccentColorSchema,
    /**
     * The store identities (D13) — `''` until the brand has a release there.
     * Turned into links by {@link storeLinksFor}, never by a client's own template.
     */
    'brand.iosAppId': z.string(),
    'brand.androidPackage': z.string(),
    /**
     * What the marketing site says about the brand (D13). All per-locale (N5)
     * and all `''` until staff write them: the site falls back to its own
     * neutral copy for the SEO fields, and draws no tagline for an empty one.
     */
    'brand.web.tagline': z.string(),
    'brand.web.seoTitle': z.string(),
    'brand.web.seoDescription': z.string(),
    /**
     * The social-sharing image, as a `brand_asset` id (D13) — served through
     * `GET /media/brand-og-image` like the logo; `null` lets the site generate
     * one from the mark and the palette.
     */
    'brand.web.ogImageMediaId': z.uuid().nullable(),
  })
  .strict();

export type PublicAppSettings = z.infer<typeof PublicAppSettingsSchema>;

/**
 * The same key set as a list, for the api's registry-parity spec.
 *
 * The house shape for a closed set a consumer must enumerate (`DISCOUNT_SOURCES`,
 * `LESSON_BLOCK_KINDS`): hand-writing the keys at the use site is a second source
 * of truth, and reading Zod's internals from product code is an internals
 * dependency behind a cast. The cast lives in this file's spec and nowhere else.
 */
export const PUBLIC_APP_SETTING_KEYS = [
  'billing.currency',
  'billing.direct.enabled',
  'billing.direct.paymentDetails',
  'brand.name',
  'brand.supportEmail',
  'brand.links',
  'brand.iosRcPublicKey',
  'brand.androidRcPublicKey',
  'brand.weekStartsOn',
  'brand.logoMediaId',
  'brand.logoDarkMediaId',
  'brand.legalEntity',
  'brand.dateFormat',
  'brand.timeFormat',
  'brand.defaultLocale',
  'brand.accentColor',
  'brand.iosAppId',
  'brand.androidPackage',
  'brand.web.tagline',
  'brand.web.seoTitle',
  'brand.web.seoDescription',
  'brand.web.ogImageMediaId',
] as const;

export type PublicAppSettingKey = (typeof PUBLIC_APP_SETTING_KEYS)[number];
